import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ImpersonationService } from '@mysrc/app/modules/fwcore/services/impersonation.service';
import { OktaWrapperService } from '@mysrc/services/framework/okta-wrapper.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { Observable, of } from 'rxjs';
import { RequestDealHistoricData } from '../models/messages/deal-historic-data-messages';
import urlJoin from 'url-join';
import { BaseHttpReqRespService } from '../../../modules/fwcore/services';
import { RequestDealImport, ResponseDealImport } from '../models/messages/deal-import-messages';
import { DealImport } from '../models/deal-import';
import { AdminService } from '../../admin/services/admin.service';
import { RequestModelUpdateAuditNames, ResponseModelUpdateAuditNames } from '../models/messages/model-update-audit-messages';

@Injectable({
  providedIn: 'root'
})

export class DealsService extends BaseHttpReqRespService {

  // ----------------- Constants ---------------------------------------------

  readonly endPointCountrySummary: string = 'models/country';
  readonly endPointCurrency: string = 'models/currency';
  readonly endPointDealGroup: string = "models/deal-group";
  readonly endPointDealTypes: string = "models/deal-type";
  readonly endPointDealStatus: string = "models/deal-status";

  readonly endPointValueOption: string = 'models/value-option';
  readonly endPointValueOptionSync: string = 'models/value-option/sync';
  readonly endPointValueOptionCascade: string = 'models/value-option-cascade';
  readonly endPointValueOptionCascadeHdr: string = 'models/value-option-cascade-hdr';
  readonly endPointValueOptionCascadeRaw: string = 'models/value-option-cascade-raw';
  readonly endPointValueChart: string = 'models/chart';
  readonly endPointValueControlType: string = 'models/value-control-type';
  readonly endPointQuCacheRefresh: string = 'models/qu-cache-refresh';

  readonly endPointValueChartGroup: string = 'models/chart-type';
  readonly endPointValueChartConfig: string = 'models/chart-config';
  readonly endPointValueChartSeries: string = 'models/chart-series';

  readonly endPointValueDataType: string = 'models/value-data-type';
  readonly endPointValueCategory: string = 'models/value-category';
  readonly endPointValueUnit: string = 'models/value-unit';
  
  readonly endPointModelValue: string = 'models/model-value';
  readonly endPointExcelModel: string = 'models/excel-model';
  readonly endPointModelValueTree: string = 'models/model-value-tree';
  readonly endPointModelValueDataCollections: string = 'models/model-value-data';
  readonly endPointModelValueReorder: string = 'models/model-value-reorder';
  readonly endPointModelValueCopyPaste: string = 'models/model-value/copy-paste';
  
  readonly endPointPercentile: string = 'models/percentile';
  readonly endPointConfidence: string = 'models/confidence';

  readonly endPointDependencyAction: string = 'models/dependency-action';
  readonly endPointDependencyOperator: string = 'models/dependency-operator';
  readonly endPointValueDependency: string = 'models/value-dependency';

  readonly endPointGlossary: string = 'models/glossary';

  // The scopes the glossary holds terms for. Each entry carries the ids to send
  // back to endPointGlossary for it - see GlossaryScope for why they differ from
  // the ids that identify the scope.
  readonly endPointGlossaryScope: string = 'models/glossary-scopes';

  // Tower admin
  readonly endPointTower: string = "models/tower";
  
  // Reports
  readonly endPointReportTemplateType: string = "models/report-type";
  readonly endPointReportTemplate: string = "models/report-template";
  readonly endPointReportTemplateLayout: string = "models/report-layout";
  readonly endPointReportBlock: string = "models/report-block";

  readonly endPointVarNameReport: string = "models/varname-report-config";

  readonly endPointDeals: string = "deals";
  readonly endPointDealItemSummary: string = 'deals/deal-item-summary';
  readonly endPointDealName: string = "deals/name";
  readonly endPointDealCopy: string = "deals/copy";
  readonly endPointDealItemCopyTo: string = "deals/copy-to";
  readonly endPointDealCalculate: string = "deals/calculate";
  readonly endPointDealState: string = "deals/state";
  readonly endPointDealDraftReset: string = "deals/draft-reset"; 
  readonly endPointDealRestore: string = "deals/restore";
  readonly endPointDealLocking: string = "deals/lock";

  readonly endPointAIConversation: string = "deals/ai-c";
  readonly endPointAIMessage: string = "deals/ai-m";
  
  readonly endPointDealChart: string = "deals/chart";
  readonly endPointDealChartSummary: string = "deals/chart-summary";
  readonly endPointTowerRate: string = 'deals/deal-tower';
  readonly endPointPercentileValues: string = 'deals/deal-percentile-value';

  readonly endPointDealGroupModule: string = 'deals/dealgroup-module';

  readonly endPointDealFolder: string = "deals/folder";
  readonly endPointDealFolderItem: string = "deals/folder-item";
  readonly endPointDealShare: string = "deals/folder-share";
  readonly endPointDealShareState: string = "deals/deal-share-state";

  readonly endPointDealQuestion: string = 'deals/deal-question';
  readonly endPointDealValue: string = 'deals/deal-value';
  readonly endPointDealValueInjected: string = 'deals/deal-value-injected';
  readonly endPointDealValueBulk: string = 'deals/deal-value-bulk';

  readonly endPointDownloadZipDealModelFiles: string = 'deals/download/model-files';
  readonly endPointDownloadPdfReport: string = 'deals/pdf-download-result';
  readonly endPointDownloadExcelDealReport: string = 'deals/download/excel-report';
  readonly endPointDownloadExcelDealDataVarNames: string = 'deals/download/excel-data';
  readonly endPointDownloadExcelDealResults: string = 'deals/download/excel-result';

  readonly endPointDealExcelDownload: string = 'deals/excel-download';
    
  readonly endPointDealReportZipDownload: string = 'deals/report-zip-download';
  readonly endPointDealResultZipDownload: string = 'deals/result-zip-download';
  readonly endPointDealVarnameZipDownload: string = 'deals/varnames-zip-download';

  //caleed only when the Ratecards are updated 
  readonly endPointRateCardUpdatedCalculate: string = 'rates/refreshforRateCardUpdate';
  // These will be relegated and consolidated into deals/calculate
  readonly endPointRateRefresh: string = 'rates/refresh';
  readonly endPointRateItems: string = 'rates/item';
  readonly endPointRateItemUser: string = 'rates/item-user';  

  // Rate
  readonly endPointRate: string = 'rates/rate';
  readonly endPointUnitPricing: string = 'rates/unit-pricing';

  // blended rate Item
  readonly endPointBlendedRateItems: string = 'rates/blended-item';
  readonly endPointBlendedGroup: string = 'rates/blended-grouplist';

  // Rate header endpoints
  readonly endPointRateHeaders: string = 'rates/header';  
  readonly endPointRateHeader: string = 'rates/rate-header';
  readonly endPointHeaderType: string = 'rates/rate-header-type';

  readonly endPointRateValidationJson: string = 'rates/validation-json';
  readonly endPointRateRowDefaults: string = 'rates/row-defaults';  

  //Exchange Rates endpoints
  readonly endPointExchangeRate: string = 'rates/exchange-rate';

  //clientuser update
  readonly endPointClientUser: string = 'models/client-user';

  readonly endPointServiceModel: string = 'models/service-model';
   
  // Email Template Management
  readonly endPointEmailHeaderFooter: string = 'models/email-header-footer';
  readonly endPointEmailTagValue: string = 'models/email-tag';
  readonly endPointEmailTemplate: string = 'models/email-template';
  readonly endPointEmailTemplateName: string = 'models/email-template-name';

  readonly endPointDealUsageByTower: string = 'deals/dealUsage-byTower';

  protected isgStormNewsEndpoint;
  protected isgStormAPIKey;

  // Deal Historic Data
  readonly endPointDealHistoricData: string = 'deals/GetDealHistoricData';
  readonly endPointDealHistoricDataExport: string = 'deals/DealHistoricDataExport';

  //Deal Import
  readonly endPointImportDealsUrl: string = 'deals/deal-import-put';
  readonly endPointImportDealGetsUrl: string = 'deals/deal-import-get';
  readonly endPointImportDealDeleteUrl: string = 'deals/deal-import-delete';
  readonly endPointDownloadTemplateteUrl: string = 'deals/ExportVarnameTemplate';
  readonly endPointExportDealsUrl: string = 'deals/deal-export';
  readonly endPointDealsByTask = "deals/by-task";
  readonly endPointDownloadExportGetsUrl = "deals/deal-import-download-zip";
  protected downloadFileUrl: string = `deals/download`;

  //Export Rate files and report

  readonly endPointExportRateTable: string = 'deals/export-ratetable';
  readonly endPointRateDealReport: string = 'deals/export-dealrate';

  //Deal share count

  readonly endPointSharedDealCountUrl: string = 'deals/shared-deal-count';
  readonly endPointTowerUpdateCountUrl: string = 'deals/updated-tower-count';


  // Diagnostics
  readonly endPointPerformance: string = 'diagnostics/performance';
  readonly endPointPerformanceUsers: string = 'diagnostics/performance-users';
  readonly endPointPerformanceDetail: string = 'diagnostics/performance-detail';
  readonly endPointPerformanceSummary: string = 'diagnostics/performance-summary';

  // Model update audit
  readonly endPointModelUpdateAudit: string = 'models/model-update-audit';

  readonly endPointRateAdj: string = 'rates/rate-adjustment';

  readonly endPointRateAdjCal: string = 'rates/rate-adjustment-cal';

  readonly endPointRateOption: string = 'models/rate-option';
  readonly endPointAddRateFile: string = 'models/add-rate-file';
  readonly endPointRateFileDownloadZip: string = 'models/rate-file-download-zip';


  /*VarNameEdpoints*/
  readonly endPointSaveVarName: string = 'models/save-varname';
  readonly endPointDeleteVarName: string = 'models/delete-varname';
  readonly endPointVarNameMove = 'models/move-varname';
  readonly endPointVarNameList: string = 'models/varname-grid';
  
  
  

  readonly endPointRateFactorDetailsGet: string = 'models/rate-factor-details';
  readonly endPointRateIndexValuesGet: string = 'models/rate-index-values';
  readonly endPointSaveRateFactorDetails: string = 'models/save-rate-factor-details';
  readonly endPointUpdateRateFactorSort: string = 'models/update-rate-factor-details-sort';
  readonly endPointRateFactorAdjustments: string = 'models/rate-factor-adjustments';

  readonly endPointExportScenariojson: string = 'deals/export-scenario-json';
  readonly endPointImportJson: string = 'deals/import-json';

  readonly endPointMaintenanceActivemsg: string = 'modules/maintenancemsg/active'

  // ----------------- Constructor ---------------------------------------------
  constructor(protected lservice: AdminService,
    http: HttpClient,
    @Inject('CONFIG_SETTINGS') config: any,                   // no modifier — constructor body only
    oktaWrapper: OktaWrapperService,                          // no modifier — passing up
    impersonation: ImpersonationService                       // no modifier — passing up
  ) {
    super(http, config, impersonation, oktaWrapper);

    const configSettings = config['general'];
    //this.baseUrl = configSettings['adminApiUrl'];
    //this.apiKey = configSettings['apiKey'];

    this.isgStormNewsEndpoint = configSettings['isgVoiceStormUrl'];
    this.isgStormAPIKey = configSettings['isgVoiceStormToken'];
  }

  getISGVoiceStormNews(): Observable<any> {

    const url = this.isgStormNewsEndpoint + "/v1/posts/categories/7?include=images&take=100";
    return this.http.get<any>(url);
  }
 
  exportDealHistoricData$(): Observable<Blob> {
    const url = urlJoin(this.baseUrl, this.endPointDealHistoricDataExport);
    const token = this.getToken();

    const headers = token ? this.getHttpBearerHeaders(token) : undefined;

    return this.http.post(url, null, { headers, responseType: 'blob' });
  }

  importDeals(model: DealImport): Observable<any> {

    let emptyResp: any = {};
    let token = this.getToken();

    if (token != null) {

      let url = urlJoin(this.baseUrl, this.endPointImportDealsUrl);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<any>(url, model, httpOptions);

    } else {
      return of(emptyResp);
    }
  }
  downloadAvaiableFile(req: string) {

    return this.lservice.getBlobDataWithoutRequest$(`${this.downloadFileUrl}/${req}`);

  }

  getDealImports(req: any): Observable<any> {

    let token = this.getToken();

    if (token) {

      let url = urlJoin(this.baseUrl, this.endPointImportDealGetsUrl);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token),
        params: req
      };

      return this.http.get<any>(url, httpOptions);

    }

    return of({});
  }

  deleteDealImportFile(model: any): Observable<any> {

    let token = this.getToken();

    if (token) {

      let url = urlJoin(this.baseUrl, this.endPointImportDealDeleteUrl);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token),
        body: model
      };

      return this.http.delete<any>(url, httpOptions);

    }

    return of({});
  }

  downloadVarnameTemplate$(applicationId: any): Observable<Blob> {
    const url = urlJoin(this.baseUrl, this.endPointDownloadTemplateteUrl);

    const token = this.getToken();
    const headers = token ? this.getHttpBearerHeaders(token) : undefined;

    return this.http.post(url, { applicationId }, {
      headers,
      responseType: 'blob'
    });
  }

  exportDeals(payload: any): Observable<Blob> {

    let url = urlJoin(this.baseUrl, this.endPointExportDealsUrl);

    const token = this.getToken();
    const headers = token ? this.getHttpBearerHeaders(token) : undefined;

    return this.http.post(url, payload, {
      headers,
      responseType: 'blob'
    });
  }

  downloadImportZip(payload: any): Observable<Blob> {
    let token = this.getToken();

    if (token) {
      let url = urlJoin(this.baseUrl, this.endPointDownloadExportGetsUrl);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token),
        params: payload,
        responseType: 'blob' as 'json'  
      };

      return this.http.get<Blob>(url, httpOptions);
    }

    return of(new Blob());
  }

  downloadRateFileZip(payload: any): Observable<Blob> {
    let token = this.getToken();

    if (token) {
      let url = urlJoin(this.baseUrl, this.endPointRateFileDownloadZip);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token),
        params: payload,
        responseType: 'blob' as 'json'
      };

      return this.http.get<Blob>(url, httpOptions);
    }

    return of(new Blob());
  }


  getSharedDealCount(req: any): Observable<any> {

    let token = this.getToken();

    if (token) {

      let url = urlJoin(
        this.baseUrl,
        this.endPointSharedDealCountUrl
      );

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<any>(
        url,
        req,
        httpOptions
      );
    }

    return of({});
  }

  getUpdatedTowerCount(req: any): Observable<any> {
    let token = this.getToken();

    if (token) {

      let url = urlJoin(
        this.baseUrl,
        this.endPointTowerUpdateCountUrl
      );

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<any>(
        url,
        req,
        httpOptions
      );
    }

    return of({});
  }

  getModelUpdateAuditNames$(req: RequestModelUpdateAuditNames): Observable<ResponseModelUpdateAuditNames> {
    return this.setData$<RequestModelUpdateAuditNames, ResponseModelUpdateAuditNames>(
      req,
      `${this.endPointModelUpdateAudit}/names`
    );
  }

}
